/**
 * Notes for drivers on a place — data hooks (#45 PR 2, DEC-359). A place is one of the
 * company's sites (LOCATION), a catalog well (WELL) or a catalog disposal facility
 * (FACILITY). The API is the gate (reads VIEW_HAULER_CATALOG, writes
 * MANAGE_HAULER_CATALOG); these make no trust decisions.
 */

import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
} from '@tanstack/react-query';
import { authedFetch, useApiOpts, useApiQuery, type WebApiOpts } from '@lodex-pro/web-shared';

export type PlaceKind = 'LOCATION' | 'WELL' | 'FACILITY';

export interface PlaceNote {
  readonly notes: string;
  readonly updatedAt: string;
  readonly updatedByName: string | null;
}

export interface NoteFacility {
  readonly id: string;
  readonly name: string;
  readonly city: string | null;
  readonly county: string | null;
  readonly state: string | null;
  readonly isActive: boolean;
  readonly note: PlaceNote | null;
}

/** The longest note the API takes (DEC-359 choice 5). */
export const MAX_NOTE_LENGTH = 2000;

export const PLACE_NOTE_KEY = (kind: PlaceKind, id: string): readonly string[] => [
  'place-notes',
  kind,
  id,
];
export const NOTE_FACILITIES_KEY = ['place-notes', 'facilities'] as const;

export function usePlaceNote(
  kind: PlaceKind,
  id: string | null,
): ReturnType<typeof useApiQuery<{ note: PlaceNote | null }>> {
  const opts = useApiOpts();
  return useApiQuery<{ note: PlaceNote | null }>(
    opts,
    `/v1/place-notes?kind=${kind}&id=${id ?? ''}`,
    {
      queryKey: PLACE_NOTE_KEY(kind, id ?? ''),
      enabled: id !== null,
      staleTime: 30_000,
      // A 403/404 here only means "show no note" (e.g. a role without the catalog view).
      retry: false,
    },
  );
}

export function useNoteFacilities(
  q: string,
  cursor: string | null,
): ReturnType<typeof useApiQuery<{ facilities: NoteFacility[]; nextCursor: string | null }>> {
  const opts = useApiOpts();
  const qs = new URLSearchParams({ limit: '50' });
  if (q.trim() !== '') qs.set('q', q.trim());
  if (cursor !== null) qs.set('cursor', cursor);
  return useApiQuery<{ facilities: NoteFacility[]; nextCursor: string | null }>(
    opts,
    `/v1/place-notes/facilities?${qs.toString()}`,
    { queryKey: [...NOTE_FACILITIES_KEY, q.trim(), cursor ?? ''], staleTime: 30_000 },
  );
}

export interface SaveNoteBody {
  readonly target: { readonly kind: PlaceKind; readonly id: string };
  /** null or blank clears the note. */
  readonly notes: string | null;
  /** The updatedAt the editor loaded; null when it loaded no note. */
  readonly expectedUpdatedAt: string | null;
}

/** The API's own reason for a refusal: VALIDATION_FAILED carries it on the field. */
async function refusalMessage(res: Response): Promise<string> {
  if (res.status === 403) return 'Your role cannot change notes for drivers.';
  const fallback = `Could not save the note (${String(res.status)}).`;
  try {
    const body = (await res.json()) as {
      error?: { message?: string; fields?: { path?: string; reason?: string }[] };
    };
    return body.error?.fields?.[0]?.reason ?? body.error?.message ?? fallback;
  } catch {
    return fallback;
  }
}

/** Thrown when someone else changed the note since it was loaded (the stale-save guard). */
export class StaleNoteError extends Error {}

async function saveNote(opts: WebApiOpts, body: SaveNoteBody): Promise<{ note: PlaceNote | null }> {
  const res = await authedFetch(opts, '/v1/place-notes', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const message = await refusalMessage(res);
    throw /changed by someone else/i.test(message)
      ? new StaleNoteError(message)
      : new Error(message);
  }
  return (await res.json()) as { note: PlaceNote | null };
}

/**
 * Save one place's note. On success the note, the facility list and any `alsoInvalidate`
 * keys (the site or well audit trail, which records the change) are refreshed.
 */
export function useSavePlaceNote(
  alsoInvalidate: readonly QueryKey[] = [],
): UseMutationResult<{ note: PlaceNote | null }, Error, SaveNoteBody> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<{ note: PlaceNote | null }, Error, SaveNoteBody>({
    mutationFn: (body) => saveNote(opts, body),
    onSuccess: (r, body) => {
      qc.setQueryData(PLACE_NOTE_KEY(body.target.kind, body.target.id), r);
      void qc.invalidateQueries({ queryKey: NOTE_FACILITIES_KEY });
      for (const key of alsoInvalidate) void qc.invalidateQueries({ queryKey: key });
    },
  });
}
